import { Suspense, useState, useTransition } from 'react';
import type { Selection } from 'react-aria-components';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Autocomplete } from '@gonfalon/ui/Autocomplete';
import type { ListBoxProps } from '@launchpad-ui/components';
import { FieldError, Input, ListBox, ListBoxItem, SearchField } from '@launchpad-ui/components';

import styles from './FilterValuesAutocomplete.module.css';

interface ValueItem {
  id: string;
  name: string;
  isNew?: boolean;
}

interface FilterValuesAutocompleteProps {
  onSelectionChange: (keys: Selection) => void;
  selectedKeys: string[];
  options?: string[];
}

export function FilterValuesAutocomplete({
  selectedKeys = [],
  options = [],
  onSelectionChange,
  ...props
}: FilterValuesAutocompleteProps) {
  const [filterText, setFilterText] = useState('');
  const [inputLength, setInputLength] = useState(0);
  const isValid = inputLength <= 140;
  const startTransition = useTransition()[1];
  return (
    <Autocomplete
      {...props}
      inputValue={filterText}
      onInputChange={(value) => startTransition(() => setFilterText(value))}
      disableAutoFocusFirst
    >
      <SearchField
        aria-label={msg('metrics.eventFilters.selectValues')}
        onChange={(e) => {
          setInputLength(e.length);
        }}
        isInvalid={!isValid}
      >
        <Input placeholder={msg('metrics.eventFilters.selectValues')} />
        {!isValid && (
          <FieldError>
            <Message id="metrics.eventFilters.valueTooLong" values={{ length: inputLength }} />
          </FieldError>
        )}
      </SearchField>
      <QuietErrorBoundary>
        <Suspense
          fallback={
            <div className={styles.fallback}>
              <div className={styles.item} />
              <div className={styles.item} />
              <div className={styles.item} />
              <div className={styles.item} />
            </div>
          }
        >
          <ValueListBox
            filterText={filterText}
            selectedKeys={selectedKeys}
            onSelectionChange={(selection) => {
              if (isValid) {
                onSelectionChange(selection);
              }
            }}
            options={options}
          />
        </Suspense>
      </QuietErrorBoundary>
    </Autocomplete>
  );
}

interface ValueListBoxProps extends ListBoxProps<ValueItem> {
  filterText: string;
  selectedKeys: string[];
  options: string[];
}

function ValueListBox({ filterText, selectedKeys, onSelectionChange, options }: ValueListBoxProps) {
  const selectedItems = selectedKeys.map((v) => ({ id: v, name: v }));

  // TODO: replace with a query for the appropriate context attribute values
  const queriedValues = options;
  const queriedItems: ValueItem[] = queriedValues.map((v) => ({ id: v, name: v }));
  const queriedItemsNotSelected = queriedItems.filter((v) => !selectedKeys.includes(v.id));

  let accessibleItems: ValueItem[];
  if (filterText) {
    accessibleItems = [...selectedItems, ...queriedItemsNotSelected.filter((v) => v.name.includes(filterText))];
    if (!selectedKeys.includes(filterText) && !queriedValues.includes(filterText)) {
      accessibleItems.unshift({ id: filterText, name: filterText, isNew: true });
    }
  } else {
    accessibleItems = [...selectedItems, ...queriedItemsNotSelected];
  }

  return (
    <ListBox
      className={styles.listbox}
      selectionMode="multiple"
      items={accessibleItems}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      aria-label={msg('metrics.eventFilters.selectValues')}
      renderEmptyState={() => (
        <span className={styles.emptyState}>
          <Message id="metrics.eventFilters.typeToAdd" />
        </span>
      )}
    >
      {(item) => (
        <ListBoxItem id={item.name} textValue={`${item.name}-new`}>
          {item.isNew ? (
            <span className={styles.new}>
              <Message
                id="metrics.eventFilters.addValue"
                values={{ name: item.name, i: (chunks) => <i>{chunks}</i> }}
              />
            </span>
          ) : (
            item.name
          )}
        </ListBoxItem>
      )}
    </ListBox>
  );
}
